<script setup lang="ts">
import { $getNodeByKey, $getRoot, $isElementNode, type ElementNode } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { watch } from "vue";

import { EVENT_BLUR_OVERALL } from "../const";

const props = defineProps<{
  hasFocus: boolean;
}>();

const editor = useLexicalComposer();

const focus = (start?: boolean) => {
  editor.getRootElement()?.focus({ preventScroll: true });
  editor.focus(undefined, { defaultSelection: start ? "rootStart" : "rootEnd" });
};

const focusEnd = () => {
  editor.update(() => {
    $getRoot().selectEnd();
  });
  editor.focus(undefined, { defaultSelection: "rootEnd" });
};

const distanceToPoint = (rect: DOMRect, clientX: number, clientY: number) => {
  const dx = Math.max(rect.left - clientX, clientX - rect.right, 0);
  const dy = Math.max(rect.top - clientY, clientY - rect.bottom, 0);
  return Math.hypot(dx, dy);
};

const findNearestBlockKey = (parent: ElementNode, clientX: number, clientY: number): string => {
  const nearest = parent.getChildren().reduce<{ node: ElementNode; distance: number } | null>((acc, child) => {
    if (!$isElementNode(child) || child.isInline()) {
      return acc;
    }
    const element = editor.getElementByKey(child.getKey());
    if (!element) {
      return acc;
    }
    const distance = distanceToPoint(element.getBoundingClientRect(), clientX, clientY);
    if (acc === null || distance < acc.distance) {
      return { node: child, distance };
    }
    return acc;
  }, null);
  if (nearest === null) {
    return parent.getKey();
  }
  return findNearestBlockKey(nearest.node, clientX, clientY);
};

const focusNearestBlock = (clientX: number, clientY: number) => {
  const result = editor.getEditorState().read(() => {
    const key = findNearestBlockKey($getRoot(), clientX, clientY);
    const element = editor.getElementByKey(key);
    if (!element) {
      return null;
    }
    return { key, atEnd: clientX > element.getBoundingClientRect().right };
  });

  if (!result) {
    return;
  }

  editor.update(() => {
    const node = $getNodeByKey(result.key);
    if (!node) {
      return;
    }
    if (result.atEnd) {
      node.selectEnd();
      return;
    }
    node.selectStart();
  });
  editor.focus(undefined, { defaultSelection: result.atEnd ? "rootEnd" : "rootStart" });
};

watch(
  () => props.hasFocus,
  (newHasFocus) => {
    if (newHasFocus) {
      return;
    }
    editor.dispatchCommand(EVENT_BLUR_OVERALL, {});
  }
);

defineExpose({
  focus,
  focusEnd,
  focusNearestBlock,
});
</script>

<template>
  <slot />
</template>
